import { useState, useRef, useEffect } from "react";
import { Send, X, Zap, Minimize2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AVATAR_URL = "/edgar-avatar-small.jpg";

function TypingDots() {
  return (
    <div className="flex items-start gap-2 px-4 py-1">
      <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 ring-1 ring-emerald-500/30">
        <img src={AVATAR_URL} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="bg-zinc-800/80 border border-zinc-700/40 rounded-xl rounded-tl-sm px-4 py-3">
        <div className="flex gap-1">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 bg-emerald-400 rounded-full"
              style={{ animation: "typing-dot 1.4s infinite", animationDelay: `${i * 0.2}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function WidgetView() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [minimized, setMinimized] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Listen for postMessage from parent to control widget
  useEffect(() => {
    const handler = (e) => {
      if (e.data === "widget-toggle") setMinimized((prev) => !prev);
      if (e.data === "widget-open") setMinimized(false);
      if (e.data === "widget-close") setMinimized(true);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    let sid = sessionId;
    if (!sid) {
      try {
        const res = await axios.post(`${API}/widget/sessions`);
        sid = res.data.id;
        setSessionId(sid);
      } catch (e) {
        console.error("Error creating session", e);
        return;
      }
    }

    const tempId = "temp-" + Date.now();
    setMessages((prev) => [...prev, { id: tempId, role: "user", content: text }]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/widget/send`, { session_id: sid, message: text });
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== tempId);
        return [...filtered, res.data.user_message, res.data.assistant_message];
      });
    } catch (e) {
      console.error("Error sending message", e);
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const suggestions = [
    "¿Cómo empiezo como agente?",
    "¿Cómo detecto talento real?",
  ];

  if (minimized) {
    return (
      <div className="h-screen w-screen flex items-end justify-end p-4 bg-transparent">
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setMinimized(false)}
          data-testid="widget-open-btn"
          className="w-14 h-14 rounded-full bg-emerald-500 text-zinc-950 flex items-center justify-center shadow-[0_4px_24px_rgba(16,185,129,0.4)] hover:bg-emerald-400 transition-colors"
          style={{ zIndex: 2147483647, position: "relative" }}
        >
          <img src={AVATAR_URL} alt="Edgar IA" className="w-full h-full rounded-full object-cover ring-2 ring-emerald-400" />
        </motion.button>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex items-end justify-end p-0 sm:p-4 bg-transparent">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        className="w-full sm:w-[380px] h-full sm:h-[560px] bg-zinc-950 sm:rounded-2xl overflow-hidden flex flex-col border border-zinc-800 shadow-[0_8px_48px_rgba(0,0,0,0.6)]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-zinc-900/80 border-b border-zinc-800 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full overflow-hidden ring-2 ring-emerald-500/30">
              <img src={AVATAR_URL} alt="Edgar IA" className="w-full h-full object-cover" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-zinc-100" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                Edgar IA
              </h3>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="text-[9px] text-zinc-500">Tutor Agente 360</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="widget-minimize-btn"
              onClick={() => setMinimized(true)}
              className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              <Minimize2 size={14} />
            </button>
            <button
              data-testid="widget-close-btn"
              onClick={() => {
                setMinimized(true);
                window.parent?.postMessage("widget-closed", "*");
              }}
              className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Messages */}
        {messages.length === 0 && !loading ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6">
            <div className="w-16 h-16 rounded-full overflow-hidden ring-4 ring-emerald-500/15 mb-4"
              style={{ animation: "pulse-glow 3s ease-in-out infinite" }}>
              <img src={AVATAR_URL} alt="Edgar IA" className="w-full h-full object-cover" />
            </div>
            <h2 className="text-lg font-bold text-zinc-100 mb-1" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Hola, soy Edgar IA
            </h2>
            <p className="text-xs text-zinc-500 text-center mb-5 leading-relaxed">
              Tu tutor personal de Agente 360. Pregúntame lo que necesites.
            </p>
            <div className="w-full space-y-2">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  data-testid={`widget-suggestion-${i}`}
                  onClick={() => sendMessage(s)}
                  className="w-full text-left px-3 py-2.5 bg-zinc-900/50 border border-zinc-800/50 rounded-xl text-xs text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200 transition-all"
                >
                  <Zap size={10} className="inline mr-1.5 text-emerald-500" />
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto py-3 space-y-1 min-h-0">
            <AnimatePresence>
              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex items-start gap-2 px-4 py-1 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
                >
                  {msg.role !== "user" && (
                    <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 ring-1 ring-emerald-500/30 mt-0.5">
                      <img src={AVATAR_URL} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div
                    data-testid={msg.role === "user" ? "widget-user-msg" : "widget-ai-msg"}
                    className={
                      msg.role === "user"
                        ? "bg-zinc-800 text-zinc-100 rounded-xl rounded-tr-sm px-3.5 py-2.5 max-w-[80%] text-xs leading-relaxed"
                        : "bg-zinc-900/80 border border-zinc-800/40 rounded-xl rounded-tl-sm px-3.5 py-2.5 max-w-[80%] text-xs leading-relaxed text-zinc-300"
                    }
                  >
                    <span className="whitespace-pre-wrap">
                      {msg.role === "user" ? msg.content : (
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                          p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
                          strong: ({ children }) => <strong className="text-zinc-100 font-semibold">{children}</strong>,
                          ul: ({ children }) => <ul className="list-disc pl-3 mb-1 space-y-0.5">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal pl-3 mb-1 space-y-0.5">{children}</ol>,
                          li: ({ children }) => <li>{children}</li>,
                          code: ({ children }) => <code className="bg-zinc-800/60 px-1 py-0.5 rounded text-[0.85em]">{children}</code>,
                        }}>{msg.content}</ReactMarkdown>
                      )}
                    </span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            {loading && <TypingDots />}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Input */}
        <div className="p-2.5 flex-shrink-0 border-t border-zinc-900">
          <form
            onSubmit={handleSubmit}
            className="bg-zinc-900/70 border border-zinc-800 rounded-full flex items-center p-1 focus-within:border-zinc-700 transition-colors"
          >
            <input
              ref={inputRef}
              data-testid="widget-chat-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Pregúntale a Edgar..."
              className="flex-1 bg-transparent text-zinc-200 placeholder-zinc-600 px-3 py-2 text-xs outline-none"
              disabled={loading}
            />
            <button
              data-testid="widget-send-btn"
              type="submit"
              disabled={!input.trim() || loading}
              className="bg-emerald-500 text-zinc-950 rounded-full p-2 transition-all hover:bg-emerald-400 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Send size={12} />
            </button>
          </form>
          <p className="text-center text-[8px] text-zinc-700 mt-1.5">Powered by Agente 360</p>
        </div>
      </motion.div>
    </div>
  );
}
